import { resolveImagePath } from '@/utils/imageMap.ts';
import type { NewsArticle, CurrentConditions } from '@/data/types';

interface NewsUpdatesProps {
  articles: NewsArticle[];
  currentConditions?: CurrentConditions;
}

export function NewsUpdates({ articles, currentConditions }: NewsUpdatesProps) {
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 border-b-4 border-orange-500 pb-2">
            News & Updates
          </h2>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* News Articles - Left Two Columns */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {articles.map((article) => (
                <article 
                  key={article.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Article Image */}
                  {article.image && (
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={resolveImagePath(article.image)}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}

                  {/* Article Content */}
                  <div className="p-6">
                    {/* Author and Date */}
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>By {article.author}</span>
                      <span className="mx-2">•</span>
                      <time dateTime={article.date}>
                        {new Date(article.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </time>
                    </div>

                    {/* Article Title */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <a 
                        href={article.url}
                        className="hover:text-orange-600 transition-colors"
                      >
                        {article.title}
                      </a>
                    </h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
          
          {/* Current Conditions - Right Column */}
          {currentConditions && (
            <div className="lg:col-span-1">
              <div className="bg-white border-t-2 border-gray-200 rounded-lg shadow-sm">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-300 pb-2">
                    {currentConditions.title}
                  </h2>
                  
                  {/* Webcam Image */}
                  <div className="mb-4">
                    <img
                      src={resolveImagePath(currentConditions.webcamImage)}
                      alt="Airport runway webcam view"
                      className="w-full rounded border shadow-sm"
                    />
                  </div>
                  
                  {/* Description with link */}
                  <p className="text-sm text-gray-600">
                    {currentConditions.description.replace(' local weather.', ' ')}
                    <a 
                      href={currentConditions.weatherLink}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {currentConditions.weatherLinkText}
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}